(L => {
	'use strict';

	const intersect = 'IntersectionObserver' in L._win;

	let observers = [];
	let id = 0;

	/**
	 * Bind individual rule
	 *
	 * @private
	 * @param {Function} conf.callback
	 * @param {$|HTMLElement|String} [conf.context=window] - parent container to bind event
	 * @param {Number} [conf.debounce=100] - debounce timing in milliseconds
	 * @param {Boolean} [conf=false] - whole element in view
	 * @param {String} [conf.namespace] - namespace the event
	 * @param {Boolean} [conf.once=false] - only execute the callback once
	 * @param {Object} [conf.scope] - callback scope
	 * @param {$|HTMLElement|String} conf.target - element to evaluate
	 * @param {Boolean} [conf.watch=true] - check event on scroll
	 */
	const _addRule = conf => {
		L.$each(conf.target, el => {
			const rule = L.$copy(conf);

			let observer;
			let wait;

			// Attach unique identifier
			rule.i = id++;
			rule.margin = rule.margin || 0;
			rule.threshold = rule.threshold || 0;

			// Capture base element
			rule.target = el;

			// Only setup watching when enabled
			if (rule.watch !== false) {
				const context = rule.context ? L.$(rule.context)[0] : (
					intersect ? null : L._win
				);

				observer = observers.find(obj =>
					obj.context === context &&
					obj.margin === rule.margin &&
					obj.threshold === rule.threshold
				);

				// Only attach event once
				if (! observer) {
					observer = {
						context: context,
						rules: [],
						margin: rule.margin,
						threshold: rule.threshold
					};

					if (intersect) {
						observer.fn = new IntersectionObserver(items => {
							items.forEach(item => {
								_run(observer, false, null, item);
							});
						}, {
							root: context,
							rootMargin: rule.margin + 'px',
							threshold: Math.max(rule.threshold, 0)
						});
					} else {
						observer.fn = () => {
							if (! wait) {
								wait = true;

								_run(observer, false);

								setTimeout(() => {
									wait = false;
								}, rule.debounce || 100);
							}
						};

						['resize', 'scroll'].forEach(event => {
							context.addEventListener(event, observer.fn, false);
						});
					}

					observers.push(observer);
				}

				observer.rules.push(rule);

				if (intersect) {
					observer.fn.observe(el);
				}
			}

			if (rule.watch === false || ! intersect) {
				_run(observer, true);
			}
		});
	};

	/**
	 * Check mapped rules for matching conditions
	 *
	 * @private
	 * @param {Object} [observer] - observation instance
	 * @param {Boolean} [init=false] - initial page load
	 * @param {Boolean|Array} [rules] - scroll rules
	 * @param {Object} [item] - intersection item
	 */
	const _run = (observer, init, rules, item) => {
		rules = rules || observer.rules;

		let i = rules.length;
		let rule;
		let el;
		let visible;

		while (i--) {
			rule = rules[i];
			el = item ? item.target : rule.target;

			if (item) {
				if (el === rule.target && (
					(rule.threshold !== -1 && item.isIntersecting &&
						item.intersectionRatio >= rule.threshold
					) || (rule.threshold === -1 && ! item.isIntersecting)
				)) {
					_exec(rule, el, observer, init);
				}
			} else {
				visible = rule.threshold !== -1;

				if (_check(rule.target, rule.threshold, rule.margin)) {
					if (rule.visible !== visible) {
						rule.visible = visible;
						_exec(rule, el, observer, init);
					}
				} else {
					rule.visible = ! visible;
				}
			}
		}
	};

	/**
	 * Execute matched rules
	 *
	 * @private
	 * @param {Object} rule
	 * @param {Object} el
	 * @param {Object} [observer] - observation instance
	 * @param {Boolean} [init=false] - initial page load
	 */
	const _exec = (rule, el, observer, init) => {
		const data = {
			target: el,
			init: init
		};

		rule.visible = rule.threshold !== -1;

		L.$exec(rule.callback, {
			args: rule.args ? [data].concat(rule.args) : data,
			scope: rule.scope
		});

		// Disable future execution if once
		if (rule.once) {
			_remove(observer, 'i', rule.i, init !== true);
		}
	};

	/**
	 * Determine if element is currently visible
	 *
	 * @private
	 * @param {HTMLElement} el - element to evaluate
	 * @param {Number} [threshold=0] - ratio of element in view
	 * @param {Number} [margin=0] - vertical intersection bound offset in pixels
	 * @returns {Boolean}
	 */
	const _check = (el, threshold = 0, margin = 0) => {
		if (! el.offsetWidth || ! L._html.contains(el)) {
			return false;
		}

		// Get position relative to the viewport
		const rect = el.getBoundingClientRect();

		if (threshold === -1) {
			return rect.left + rect.width < 0 ||
				rect.top + rect.height < 0 ||
				rect.left > L._win.innerWidth ||
				rect.top > L._win.innerHeight;
		}

		return rect.top + (rect.height * (1 - threshold)) - margin > -1 &&
			rect.left + (rect.width * (1 - threshold)) > -1 &&
			rect.top + (rect.height * threshold) - margin <= (L._win.innerHeight || L._html.clientHeight) &&
			rect.left + (rect.width * threshold) <= (L._win.innerWidth || L._html.clientWidth);
	};

	/**
	 * Remove scroll event
	 *
	 * @private
	 * @param {Object} observer
	 * @param {String} key
	 * @param {String} value
	 * @param {Boolean} [unbind=false]
	 * @returns {Boolean}
	 */
	const _remove = (observer, key, value, unbind) => {
		observer.rules = observer.rules.filter(event => event[key] !== value);

		if (! unbind || observer.rules.length) {
			return true;
		}

		if (intersect) {
			observer.fn.disconnect();
		} else {
			['resize', 'scroll'].forEach(event => {
				observer.context.removeEventListener(event, observer.fn);
			});
		}

		return false;
	};

	L.scroll = {
		/**
		 * Determine if elements are currently visible
		 *
		 * @param {$|HTMLElement|String} sel - elements to evaluate
		 * @param {Number} [threshold=0] - ratio of element in view
		 * @param {Number} [margin=0] - vertical intersection bound offset in pixels
		 * @returns {Boolean}
		 */
		visible(sel, threshold, margin) {
			let visible = false;

			L.$each(sel, el => {
				if (visible) {
					return;
				}

				visible = _check(el, threshold, margin);
			});

			return visible;
		},

		/**
		 * Map conditional events to element scroll display
		 *
		 * @param {Array|Object} rules - visibility rules
		 * @param {Array} [rules.args] - callback arguments
		 * @param {Function} rules.callback
		 * @param {$|HTMLElement|String} [rules.context=window] - parent container to bind event
		 * @param {Number} [rules.debounce=100] - fallback debounce timing in milliseconds
		 * @param {Number} [rules.margin=0] - vertical intersection bound offset in pixels
		 * @param {Number} [rules.threshold=0] - ratio of element in view
		 * @param {String} [rules.namespace] - namespace the event
		 * @param {Boolean} [rules.once=false] - only execute the callback once
		 * @param {Object} [rules.scope] - callback scope
		 * @param {$|HTMLElement|String} rules.target - element to evaluate
		 * @param {Boolean} [rules.watch=true] - check event on scroll
		 * @param {Object} [options] - common settings merged into each rule
		 */
		map(rules, options) {
			const sets = L.$toArray(rules);

			let i = sets.length;

			while (i--) {
				_addRule(
					Object.assign(sets[i], options)
				);
			}
		},

		/**
		 * Trigger intersection evaluation
		 */
		trigger() {
			observers.forEach(observer => {
				_run(observer, false);
			});
		},

		/**
		 * Remove events from bound rules
		 *
		 * @param {String} [namespace] - remove scroll events in this namespace
		 */
		reset(namespace) {
			observers = observers.filter(observer => _remove(observer, 'namespace', namespace, true));
		}
	};
})(Apt);